import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Text } from 'grommet';
import {
  BarChart, Bar, AreaChart, Area,
  CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { MetricCard } from '@Components/Partial/Product/Analytics';
import {
  StyledPanel,
  StyledChartWrap,
  StyledScrollWrap,
  StyledTable,
} from './StyledCidAnalyticsDashboardPage';
import {
  CHART_TOOLTIP, AXIS_TICK, GRID_STYLE,
  formatMoney, formatViews,
  PanelTitle, EmptyChart, EmptyTable,
} from './CidAnalyticsShared';

// ─── Colors ───────────────────────────────────────────────────────────────────

const AUDIT_COLORS = {
  count: '#6366F1',
  revenueLost: '#EF4444',
  monthlyRecovery: '#10B981',
  views: '#F59E0B',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRevenueRange(low, high) {
  if (!low && !high) return '—';
  if (low === high) return formatMoney(low);
  return `${formatMoney(low)} – ${formatMoney(high)}`;
}

function StatusPills({ statusBreakdown, page }) {
  const order = ['completed', 'running', 'pending', 'failed'];
  const colors = {
    completed: '#10B981',
    running: '#6366F1',
    pending: '#F59E0B',
    failed: '#EF4444',
  };

  const pills = order
    .filter((s) => statusBreakdown[s] > 0)
    .map((s) => (
      <Box key={s} direction="row" align="center" gap="0.3rem">
        <Box
          width="8px"
          height="8px"
          round="full"
          flex={false}
          style={{ background: colors[s] }}
        />
        <Text size="0.78rem" color={page.secondaryText}>
          {statusBreakdown[s].toLocaleString()}
          {' '}
          {s}
        </Text>
      </Box>
    ));

  return (
    <Box direction="row" gap="1rem" align="center" flex={false} wrap>
      {pills}
    </Box>
  );
}

StatusPills.propTypes = {
  statusBreakdown: PropTypes.object.isRequired,
  page: PropTypes.object.isRequired,
};

function AuditsPerMonthChart({ data, loading }) {
  if (!data || data.length === 0) return <EmptyChart loading={loading} />;
  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={32} />
          <Tooltip
            {...CHART_TOOLTIP}
            formatter={(value) => [value, 'Audits']}
          />
          <Bar dataKey="count" fill={AUDIT_COLORS.count} radius={[4, 4, 0, 0]} maxBarSize={36} />
        </BarChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

AuditsPerMonthChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

function AvgRevenueLostChart({ data, loading }) {
  if (!data || data.length === 0) return <EmptyChart loading={loading} />;
  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <defs>
            <linearGradient id="gradRevLost" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={AUDIT_COLORS.revenueLost} stopOpacity={0.2} />
              <stop offset="95%" stopColor={AUDIT_COLORS.revenueLost} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradMonthlyRecovery" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={AUDIT_COLORS.monthlyRecovery} stopOpacity={0.2} />
              <stop offset="95%" stopColor={AUDIT_COLORS.monthlyRecovery} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={AXIS_TICK} tickLine={false} axisLine={false} width={56} />
          <Tooltip
            {...CHART_TOOLTIP}
            formatter={(value, name) => [
              formatMoney(value),
              name === 'avgRevenueLost' ? 'Avg. Revenue Lost' : 'Avg. Monthly Recovery',
            ]}
          />
          <Area type="monotone" dataKey="avgRevenueLost" stroke={AUDIT_COLORS.revenueLost} strokeWidth={2} fill="url(#gradRevLost)" dot={false} activeDot={{ r: 4 }} />
          <Area type="monotone" dataKey="avgMonthlyRecovery" stroke={AUDIT_COLORS.monthlyRecovery} strokeWidth={2} fill="url(#gradMonthlyRecovery)" dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

AvgRevenueLostChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export default function CidAnalyticsYtAuditsTab({
  ytAudits, loading, page, small, isAdmin,
}) {
  const overview = ytAudits?.overview ?? null;
  const byMonth = ytAudits?.byMonth ?? null;
  const statusBreakdown = ytAudits?.statusBreakdown ?? {};
  const audits = ytAudits?.audits ?? null;

  return (
    <Box gap="1rem">

      {/* Status breakdown pills */}
      {Object.keys(statusBreakdown).length > 0 && (
        <Box direction="row" align="center" gap="0.5rem" flex={false}>
          <Text size="0.78rem" weight={600} color={page.secondaryText}>Pipeline:</Text>
          <StatusPills statusBreakdown={statusBreakdown} page={page} />
        </Box>
      )}

      {/* KPI row 1 */}
      <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="TOTAL AUDITS"
          value={overview ? overview.totalAudits.toLocaleString() : 'Data unavailable'}
          subLabel="completed audits in period"
          textColor={AUDIT_COLORS.count}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={AUDIT_COLORS.count}
        />
        <MetricCard
          label="AVG. EST. REVENUE LOST"
          value={overview ? formatRevenueRange(overview.avgRevenueLostLow, overview.avgRevenueLostHigh) : 'Data unavailable'}
          subLabel="average estimated loss per audit"
          textColor={AUDIT_COLORS.revenueLost}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={AUDIT_COLORS.revenueLost}
        />
        <MetricCard
          label="AVG. MONTHLY RECOVERY"
          value={overview ? formatRevenueRange(overview.avgMonthlyRecoveryLow, overview.avgMonthlyRecoveryHigh) : 'Data unavailable'}
          subLabel="avg. forecasted monthly earnings if protected"
          textColor={AUDIT_COLORS.monthlyRecovery}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={AUDIT_COLORS.monthlyRecovery}
        />
        <MetricCard
          label="TOTAL INFRINGING VIEWS"
          value={overview ? formatViews(overview.totalCombinedViews) : 'Data unavailable'}
          subLabel="combined views across all pirated videos"
          textColor={AUDIT_COLORS.views}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={AUDIT_COLORS.views}
        />
      </Grid>

      {/* KPI row 2 */}
      <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="AVG. REUPLOADS FOUND"
          value={overview ? Math.round(overview.totalReuploads / (overview.totalAudits || 1)).toLocaleString() : 'Data unavailable'}
          subLabel="avg. infringing videos per audit"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
        <MetricCard
          label="AVG. INFRINGING CHANNELS"
          value={overview ? Math.round(overview.totalInfringingChannels / (overview.totalAudits || 1)).toLocaleString() : 'Data unavailable'}
          subLabel="avg. distinct pirating channels per audit"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
        <MetricCard
          label="AVG. VIDEOS SCANNED"
          value={overview ? String(overview.avgVideosScanned) : 'Data unavailable'}
          subLabel="original videos analyzed per audit"
          textColor={page.primaryText}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
        />
      </Grid>

      {/* Charts */}
      <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
        <StyledPanel gap="0.75rem">
          <PanelTitle label="Audits per Month" />
          <AuditsPerMonthChart data={byMonth} loading={loading} />
        </StyledPanel>

        <StyledPanel gap="0.75rem">
          <PanelTitle label="Avg. Revenue Lost & Recovery per Month" />
          <AvgRevenueLostChart data={byMonth} loading={loading} />
        </StyledPanel>
      </Grid>

      {/* Audit table */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="All Audits" />
        {audits && audits.length > 0 ? (
          <StyledScrollWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Email</th>
                  {isAdmin && <th style={{ textAlign: 'left' }}>Admin</th>}
                  <th style={{ textAlign: 'left' }}>Channel</th>
                  <th>Est. Revenue Lost</th>
                  <th>Monthly Recovery</th>
                  <th>Reuploads</th>
                  <th>Infringing Channels</th>
                  <th>Videos Scanned</th>
                  <th>Combined Views</th>
                  <th style={{ textAlign: 'left' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {audits.map((row, i) => (
                  <tr key={row.auditId || i}>
                    <td style={{ textAlign: 'left', color: 'var(--page-hint-text)' }}>{i + 1}</td>
                    <td style={{ textAlign: 'left', fontSize: '0.8rem' }}>
                      {row.email || <span style={{ color: 'var(--page-hint-text)' }}>—</span>}
                    </td>
                    {isAdmin && (
                      <td style={{ textAlign: 'left', fontSize: '0.8rem', color: 'var(--page-hint-text)' }}>
                        {row.adminUserId ? `#${row.adminUserId}` : '—'}
                      </td>
                    )}
                    <td style={{ textAlign: 'left' }}>
                      {row.channelUrl ? (
                        <a
                          href={row.channelUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--page-link)' }}
                        >
                          {row.channelName || row.channelUrl}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--page-hint-text)' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: AUDIT_COLORS.revenueLost, fontWeight: 600 }}>
                      {row.summary
                        ? formatRevenueRange(
                          row.summary.estimatedRevenueLostLow,
                          row.summary.estimatedRevenueLostHigh,
                        )
                        : '—'}
                    </td>
                    <td style={{ color: AUDIT_COLORS.monthlyRecovery }}>
                      {row.summary
                        ? formatRevenueRange(
                          row.summary.estimatedMonthlyRecoveryLow,
                          row.summary.estimatedMonthlyRecoveryHigh,
                        )
                        : '—'}
                    </td>
                    <td>{row.summary?.totalReuploadsFound?.toLocaleString() ?? '—'}</td>
                    <td>{row.summary?.uniqueInfringingChannels?.toLocaleString() ?? '—'}</td>
                    <td>{row.summary?.videosScanned?.toLocaleString() ?? '—'}</td>
                    <td style={{ color: AUDIT_COLORS.views }}>
                      {row.summary ? formatViews(row.summary.combinedReuploadViews) : '—'}
                    </td>
                    <td style={{ textAlign: 'left', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </StyledScrollWrap>
        ) : <EmptyTable page={page} loading={loading} />}
      </StyledPanel>

    </Box>
  );
}

CidAnalyticsYtAuditsTab.propTypes = {
  ytAudits: PropTypes.shape({
    overview: PropTypes.shape({
      totalAudits: PropTypes.number,
      avgRevenueLostLow: PropTypes.number,
      avgRevenueLostHigh: PropTypes.number,
      avgMonthlyRecoveryLow: PropTypes.number,
      avgMonthlyRecoveryHigh: PropTypes.number,
      totalCombinedViews: PropTypes.number,
      totalReuploads: PropTypes.number,
      totalInfringingChannels: PropTypes.number,
      avgVideosScanned: PropTypes.number,
    }),
    byMonth: PropTypes.arrayOf(PropTypes.shape({
      month: PropTypes.string,
      count: PropTypes.number,
      avgRevenueLost: PropTypes.number,
      avgMonthlyRecovery: PropTypes.number,
    })),
    statusBreakdown: PropTypes.object,
    audits: PropTypes.array,
  }),
  loading: PropTypes.bool.isRequired,
  page: PropTypes.object.isRequired,
  small: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool,
};
